import React from 'react'
import ReactDOM from 'react-dom/client'
import { mergeDeepRight } from 'ramda'
import axios from 'axios'
import applyConverters from 'axios-case-converter'
import LocalStorageService from 'services/LocalStorageService'
import { KEYS } from 'utils/localStorage'
import { PATHS } from 'utils/paths'
import styled from 'styled-components'

import { apiUrl } from 'utils/apiUrl'
import { rememberPageAtLogout } from 'utils/postLoginDestination'

export const mergeConfig = config => {
  const authToken = LocalStorageService.get(KEYS.token)
  const authHeader = authToken
    ? {
        Authorization: authToken.includes('Bearer')
          ? authToken
          : `Bearer ${authToken}`
      }
    : {}

  const defaultConfig = {
    headers: {
      ...authHeader
    }
  }
  return mergeDeepRight(defaultConfig)(config)
}

const get =
  instance =>
  async (url, config = {}) => {
    return instance.get(url, mergeConfig(config))
  }

const post =
  instance =>
  async (url, data = {}, config = {}) => {
    return instance.post(url, data, mergeConfig(config))
  }

const put =
  instance =>
  async (url, data = {}, config = {}) => {
    return instance.put(url, data, mergeConfig(config))
  }

const patch =
  instance =>
  async (url, data = {}, config = {}) => {
    return instance.patch(url, data, mergeConfig(config))
  }

const handleDelete =
  instance =>
  async (url, data = {}, config = {}) => {
    return instance.delete(
      url,
      mergeConfig(data ? { ...config, data } : config)
    )
  }

export default (baseURL = apiUrl) => {
  const instance = applyConverters(
    axios.create({
      baseURL,
      withCredentials: true,
      withXSRFToken: true,
      xsrfCookieName: 'XSRF-TOKEN',
      xsrfHeaderName: 'X-XSRF-TOKEN'
    }),
    {
      // Preserve dot-numeric keys (e.g. CPSE.7 sub-enquiry ids "1.1", "10.3", "17.1")
      // — `camel-case` would rewrite them to "1_1" and break round-trip writes.
      preservedKeys: (input) => {
        if (input === '_method' || input === '_field_comments') return true
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input)) return true
        return /^\d+\.\d+$/.test(input)
      }
    }
  )

  instance.interceptors.response.use(
    response => {
      return response
    },
    error => {
      const { response } = error

      const isAuthenticationRequest =
        error.config &&
        error.config.url.includes('/login')

      if (
        response &&
        [401, 419].includes(response.status) &&
        !isAuthenticationRequest
      ) {
        const currentPathname = window.location.pathname

        const allowedPaths = [
          PATHS.login,
          PATHS.loginCallback,
          PATHS.requestPasswordReset,
          PATHS.passwordReset,
          PATHS.emailVerification,
          PATHS.signup,
          PATHS.signupConfirm,
          PATHS.abandonedCart
        ]

        const sentHeaders = error.config?.headers
        const sentAuth = String((typeof sentHeaders?.get === 'function' ? sentHeaders.get('Authorization') : null) || sentHeaders?.Authorization || sentHeaders?.authorization || '')
        const currentToken = LocalStorageService.get(KEYS.token)
        const replacedSinceSent = !!currentToken && !!sentAuth && !sentAuth.endsWith(currentToken.replace(/^Bearer\s+/, ''))

        if (!replacedSinceSent && !allowedPaths.some(el => el.includes(currentPathname))) {
          rememberPageAtLogout()
          LocalStorageService.remove(KEYS.token)
          // Fire a custom event so the session saga can react with toast +
          // redirect. Backgrounded tabs throttle the 60s poll, so a 401 on
          // tab return would otherwise sit silently until the next tick.
          try {
            window.dispatchEvent(new CustomEvent('app:session-expired', {
              detail: { status: response.status }
            }))
          } catch (e) { /* noop */ }
        }
      }

      return Promise.reject(error)
    }
  )

  return {
    get: get(instance),
    post: post(instance),
    put: put(instance),
    patch: patch(instance),
    delete: handleDelete(instance)
  }
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 999999999999;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f2f2f2;
`

const InfoWrapper = styled.div`
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`

const Header = styled.div`
  color: black;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
`

const GoBackButton = styled.button`
  background: #33becc;
  padding: 10px 20px;
  color: #fff;
  outline: none;
  border: none;
  border-radius: 4px;
`
